<p-dialog
  resize-safe="1"
  [(visible)]="visible"
  [modal]="true"
  appendTo="body"
  (onHide)="close()"
  styleClass="log-time-dialog-modern"
  maskStyleClass="log-time-dialog-mask"
  [baseZIndex]="99999"
  [draggable]="false"
  [resizable]="false"
>
  <ng-template pTemplate="header">
    <div class="modern-header">
      <div class="header-copy">
        <span class="header-kicker">{{
          isEditMode ? "Refine existing entry" : "Create fresh entry"
        }}</span>
        <h3 class="header-title">
          {{ isEditMode ? "Edit time log" : "Log time" }}
        </h3>
        <p class="header-subtitle">
          {{
            isEditMode
              ? "Adjust duration, notes, and assignment without changing the underlying workflow."
              : "Capture accurate work duration, notes, and billing context for this activity."
          }}
        </p>
      </div>
      <!-- Task clickable section (only show when NOT editable) -->
      <div
        class="task-context"
        *ngIf="task && !taskEditable"
        (click)="navigateToTask()"
      >
        <span class="task-context-icon">
          <i class="ri-arrow-right-up-line"></i>
        </span>
        <div class="task-context-copy">
          <span class="task-label">Task</span>
          <span class="task-name">{{ taskName }}</span>
        </div>
      </div>
    </div>
  </ng-template>

  <form [formGroup]="logTimeForm" (ngSubmit)="onSubmit()" resize-safe="1">
    <div class="modern-form-content pmt-logtime-design" resize-safe="1">
      <!-- Project/Task Selection Section (only show if editable) -->
      <div
        class="section-card project-task-selection"
        *ngIf="projectEditable || taskEditable"
      >
        <div class="section-head">
          <div>
            <h4 class="section-title">Project and task</h4>
            <p class="section-caption">
              Select the destination for this time entry before logging it.
            </p>
          </div>
        </div>
        <div class="row g-3">
          <!-- Project Autocomplete -->
          <div class="col-6">
            <div class="form-field">
              <label for="project">
                Project <span class="required">*</span>
              </label>
              <p-autoComplete
                *ngIf="projectEditable"
                formControlName="project"
                [suggestions]="projectOptions"
                (completeMethod)="autoCompleteProjects($event)"
                field="name"
                [minLength]="0"
                [completeOnFocus]="true"
                (onSelect)="onProjectChange($event)"
                emptyMessage="No projects found."
                placeholder="Select project..."
                appendTo="body"
                styleClass="w-full"
              >
                <ng-template let-project pTemplate="item">
                  <div class="autocomplete-item">
                    <i class="pi pi-building"></i>
                    <span>{{ project.name }}</span>
                  </div>
                </ng-template>
              </p-autoComplete>

              <!-- Readonly display when not editable -->
              <input
                *ngIf="!projectEditable"
                type="text"
                class="form-control"
                [value]="logTimeForm.get('project')?.value?.name || projectName"
                readonly
                disabled
              />

              <small
                class="error-message"
                *ngIf="
                  logTimeForm.get('project')?.invalid &&
                  logTimeForm.get('project')?.touched
                "
              >
                Please select a project
              </small>
            </div>
          </div>

          <!-- Task Autocomplete -->
          <div class="col-6">
            <div class="form-field">
              <label for="task"> Task <span class="required">*</span> </label>
              <p-autoComplete
                *ngIf="taskEditable"
                formControlName="task"
                [suggestions]="taskOptions"
                (completeMethod)="autoCompleteTasks($event)"
                field="title"
                [minLength]="0"
                [completeOnFocus]="true"
                [disabled]="!logTimeForm.get('project')?.value"
                emptyMessage="No tasks found. Select a project first."
                placeholder="Select task..."
                appendTo="body"
                styleClass="w-full"
                (onSelect)="onTaskChange($event)"
              >
                <ng-template let-task pTemplate="item">
                  <div class="autocomplete-item">
                    <i class="pi pi-check-square"></i>
                    <span class="task-number">{{ task.uniqueTaskID }}</span>
                    <span>{{ task.title }}</span>
                  </div>
                </ng-template>
              </p-autoComplete>

              <!-- Readonly display when not editable -->
              <input
                *ngIf="!taskEditable"
                type="text"
                class="form-control"
                [value]="logTimeForm.get('task')?.value?.title || taskName"
                readonly
                disabled
              />

              <small
                class="error-message"
                *ngIf="
                  logTimeForm.get('task')?.invalid &&
                  logTimeForm.get('task')?.touched
                "
              >
                Please select a task
              </small>
            </div>
          </div>
        </div>

        <!-- Subtask row (full width, shown when isSubtask or subtask selected) -->
        <div
          class="row g-3 mt-3"
          *ngIf="isSubtask || logTimeForm.get('subtask')?.value"
        >
          <div class="col-6">
            <div class="form-field">
              <label
                >Subtask
                <span style="font-size: 11px; color: #94a3b8"
                  >(optional)</span
                ></label
              >
              <p-autoComplete
                *ngIf="subtaskEditable"
                formControlName="subtask"
                [suggestions]="subtaskOptions"
                (completeMethod)="autoCompleteSubtasks($event)"
                field="title"
                [minLength]="0"
                [completeOnFocus]="true"
                [disabled]="!logTimeForm.get('task')?.value"
                emptyMessage="No subtasks found."
                placeholder="Select subtask..."
                appendTo="body"
                styleClass="w-full"
              >
                <ng-template let-sub pTemplate="item">
                  <div class="autocomplete-item">
                    <i class="pi pi-sitemap"></i>
                    <span>{{ sub.title }}</span>
                  </div>
                </ng-template>
              </p-autoComplete>
              <input
                *ngIf="!subtaskEditable"
                type="text"
                class="form-control"
                [value]="logTimeForm.get('subtask')?.value?.title || ''"
                readonly
                disabled
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Row 1: Date, Start time, End time (4,4,4) -->
      <div class="section-card lt-schedule-card">
        <div class="section-head">
          <div>
            <h4 class="section-title">Schedule</h4>
            <p class="section-caption">
              Set the working date and the exact time range.
            </p>
          </div>
        </div>
        <div class="row g-3">
          <div class="col-4">
            <div class="form-field">
              <label for="date">Date <span class="required">*</span></label>
              <p-calendar
                inputId="date"
                formControlName="startTime"
                dateFormat="dd M yy"
                [appendTo]="'body'"
                [touchUI]="false"
                [baseZIndex]="100000"
                (onSelect)="onDateSelect($event)"
                (onBlur)="onDateBlur()"
              ></p-calendar>
            </div>
          </div>
          <div class="col-4">
            <div class="form-field">
              <label for="startTime">Start time</label>
              <div
                class="lt-time-wrap"
                (click)="openTimePicker('start', $event)"
              >
                <i class="ri-time-line lt-time-icon"></i>
                <span class="lt-time-val">{{
                  startTimeStr || "hh:mm AM"
                }}</span>
              </div>
            </div>
          </div>
          <div class="col-4">
            <div class="form-field">
              <label for="endTime">End time</label>
              <div class="lt-time-wrap" (click)="openTimePicker('end', $event)">
                <i class="ri-time-line lt-time-icon"></i>
                <span class="lt-time-val">{{ endTimeStr || "hh:mm AM" }}</span>
              </div>
            </div>
          </div>
        </div>
        <!-- Time picker dropdown — inside section-card, position absolute -->
        <div
          class="lt-picker-dropdown"
          *ngIf="activeTimePicker"
          [class.lt-picker-start]="activeTimePicker === 'start'"
          [class.lt-picker-end]="activeTimePicker === 'end'"
          (click)="$event.stopPropagation()"
        >
          <ng-container *ngTemplateOutlet="pickerGrid"></ng-container>
        </div>
      </div>

      <!-- Row 2: Duration (Hours, Mins, Chips) + Team Members ((3,3),6) -->
      <div class="section-card">
        <div class="section-head">
          <div>
            <h4 class="section-title">Duration and assignment</h4>
            <p class="section-caption">
              Fine-tune the total time and choose who this entry applies to.
            </p>
          </div>
        </div>
        <div class="row g-3">
          <!-- Duration Section: Hours, Mins, and Chips (col-6) -->
          <div class="col-6">
            <div class="duration-section">
              <div class="row g-3">
                <div class="col-6">
                  <div class="form-field">
                    <label for="hours"
                      >Hours <span class="required">*</span></label
                    >
                    <p-inputNumber
                      inputId="hours"
                      formControlName="durationHours"
                      [min]="0"
                      [showButtons]="false"
                      (onInput)="onDurationChange()"
                    ></p-inputNumber>
                  </div>
                </div>
                <div class="col-6">
                  <div class="form-field">
                    <label for="mins"
                      >Mins <span class="required">*</span></label
                    >
                    <p-inputNumber
                      inputId="mins"
                      formControlName="durationMinutes"
                      [min]="0"
                      [max]="59"
                      [showButtons]="false"
                      (onInput)="onDurationChange()"
                    ></p-inputNumber>
                  </div>
                </div>
              </div>
              <!-- Quick Duration Chips -->
              <div class="duration-presets">
                <button
                  *ngFor="let preset of durationPresets"
                  type="button"
                  class="duration-chip"
                  (click)="setQuickDuration(preset)"
                >
                  {{ preset >= 60 ? preset / 60 + "h" : preset + "m" }}
                </button>
              </div>
            </div>
          </div>

          <!-- Team Members (col-6) -->
          <div class="col-6">
            <div class="form-field">
              <label for="users">Team Members</label>
              <app-user-selector
                [projectId]="projectId"
                [users]="projectMembers"
                [selectedUserIds]="logTimeForm.get('userIds')?.value || []"
                [currentUserId]="currentUser?._id"
                [multiSelect]="true"
                [quickSelect]="true"
                placeholder="Select team members..."
                (selectedUsersChange)="onUsersSelected($event)"
              ></app-user-selector>
            </div>
          </div>
        </div>
      </div>

      <!-- Description -->
      <div class="section-card notes-section">
        <div class="section-head">
          <div>
            <h4 class="section-title">Work notes</h4>
            <p class="section-caption">
              Add a concise summary so the log is useful later in reports and
              invoices.
            </p>
          </div>
        </div>
        <div class="form-field description-field">
          <label for="description"
            >Description <span class="required">*</span></label
          >
          <textarea
            pInputTextarea
            id="description"
            formControlName="description"
            rows="5"
            autoResize="autoResize"
            placeholder="Add notes about this time log..."
          ></textarea>
          <small
            class="error-message"
            *ngIf="
              logTimeForm.get('description')?.invalid &&
              logTimeForm.get('description')?.touched
            "
          >
            Please enter a description
          </small>
        </div>
      </div>

      <!-- Billable Checkbox -->
      <div class="section-card billable-section">
        <div class="form-field checkbox-field">
          <p-checkbox
            formControlName="isBillable"
            [binary]="true"
            inputId="isBillable"
          ></p-checkbox>
          <label for="isBillable" class="checkbox-copy">
            <span class="checkbox-label">Mark as billable</span>
            <span class="checkbox-hint">
              Include this time entry in billable reporting and client
              invoicing.
            </span>
          </label>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="modern-footer">
      <small class="team-count-message" *ngIf="selectedUserCount > 1">
        Time will be logged for {{ selectedUserCount }} team member{{
          selectedUserCount > 1 ? "s" : ""
        }}
      </small>
      <div class="button-group">
        <button
          type="button"
          label="Cancel"
          class="btn-theme-grey"
          (click)="close()"
        >
          Cancel
        </button>
        <button
          pButton
          type="submit"
          [label]="isEditMode ? 'Update time log' : 'Log time'"
          class="btn-theme1"
          [loading]="isSubmitting"
          [disabled]="isSubmitting"
        ></button>
      </div>
    </div>
  </form>

  <!-- Shared picker grid template -->
  <ng-template #pickerGrid>
    <div class="lt-picker-section">
      <span class="lt-picker-label">Hour</span>
      <div class="lt-picker-grid">
        <button
          *ngFor="let h of hours"
          type="button"
          class="lt-picker-cell"
          [class.active]="pickerHour === h"
          (click)="selectHour(h)"
        >
          {{ h }}
        </button>
        <button
          type="button"
          class="lt-picker-cell lt-period"
          [class.active]="pickerPeriod === 'AM'"
          (click)="selectPeriod('AM')"
        >
          AM
        </button>
        <button
          type="button"
          class="lt-picker-cell lt-period"
          [class.active]="pickerPeriod === 'PM'"
          (click)="selectPeriod('PM')"
        >
          PM
        </button>
      </div>
    </div>
    <div class="lt-picker-section">
      <span class="lt-picker-label">Minute</span>
      <div class="lt-picker-grid">
        <button
          *ngFor="let m of minutes"
          type="button"
          class="lt-picker-cell"
          [class.active]="pickerMinute === m"
          (click)="selectMinute(m)"
        >
          {{ m < 10 ? "0" + m : m }}
        </button>
      </div>
    </div>
  </ng-template>
</p-dialog>
